import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/Badge';
import { Plus, UserCircle, Trash2, Link2, Copy, CheckCircle, Search, Pencil } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface PublisherRow {
  id: number;
  subId: string;
  displayName?: string | null;
  qualityScore?: number;
  isActive: boolean;
  campaignIds?: number[];
}

export function PublishersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editFor, setEditFor] = useState<PublisherRow | null>(null);
  const [linksFor, setLinksFor] = useState<{ id: number; subId: string; campaignIds?: number[] } | null>(null);

  const list = useQuery({
    queryKey: ['publishers'],
    queryFn: () => apiClient.get<PublisherRow[]>('/api/v1/publishers'),
  });

  // Campaign lookup for the table's Campaign column. Cached 5 min — campaigns
  // change infrequently relative to publisher list views. Built once into a
  // Map so each row render is O(1) lookup instead of N×M scan.
  const campaignsList = useQuery({
    queryKey: ['campaigns-lookup-publishers'],
    queryFn: () => apiClient.get<{ id: number; slug: string; displayName: string }[]>('/api/v1/campaigns'),
    staleTime: 5 * 60_000,
  });
  const campaignById = new Map((campaignsList.data ?? []).map((c) => [c.id, c]));

  // D8: helper to invalidate both the page list and the lookup cache used by
  // usePublisherLookup (key: 'publishers-lookup'). Forgetting the lookup key
  // meant Overviews/Statistics kept showing stale publisher names after mutations.
  const invalidatePublishers = () => {
    qc.invalidateQueries({ queryKey: ['publishers'] });
    qc.invalidateQueries({ queryKey: ['publishers-lookup'] });
  };

  const create = useMutation({
    mutationFn: (d: any) => apiClient.post('/api/v1/publishers', d),
    onSuccess: () => {
      invalidatePublishers();
      setOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.patch(`/api/v1/publishers/${id}`, data),
    onSuccess: () => {
      invalidatePublishers();
      setEditFor(null);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, hard }: { id: number; hard?: boolean }) =>
      apiClient.delete(`/api/v1/publishers/${id}${hard ? '?hard=true' : ''}`),
    onSuccess: () => invalidatePublishers(),
  });

  const handleArchiveOrDelete = async (pub: { id: number; subId: string }) => {
    const result = await confirm({
      title: `Delete ${pub.subId}?`,
      description: 'Permanently removes the sub-publisher. Past clicks/conversions keep their publisher_id but lose the sub_id.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') archive.mutate({ id: pub.id, hard: true });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">Sub-Publishers</h1>
          <p className="text-sm text-text-secondary font-mono">// {list.data?.length ?? 0} affiliates yang Anda kelola</p>
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setOpen(true)}>New Sub-Publisher</Button>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Sub ID</th>
                <th>Display Name</th>
                <th>Campaign</th>
                <th>Quality</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((p) => {
                // Single-campaign-per-publisher: resolve the first (and only)
                // assigned campaign for display. Show fallback labels for the
                // two "no assignment" cases:
                //   - empty array  → unscoped (legacy / opt-out)
                //   - id not found → campaign deleted while assignment lingered
                const assignedId = p.campaignIds?.[0];
                const assignedCampaign = assignedId != null ? campaignById.get(assignedId) : null;
                return (
                  <tr key={p.id}>
                    <td><code className="font-mono text-neon-cyan text-xs">{p.subId}</code></td>
                    <td>{p.displayName ?? <span className="text-text-tertiary">-</span>}</td>
                    <td>
                      {assignedCampaign ? (
                        // Slug shown via title tooltip (hover) instead of a
                        // second line — admin asked for a cleaner column.
                        <span
                          className="font-mono text-xs text-neon-cyan"
                          title={`slug: ${assignedCampaign.slug}`}
                        >
                          {assignedCampaign.displayName}
                        </span>
                      ) : assignedId != null ? (
                        // Orphan reference — campaign was deleted but
                        // publisher_campaigns row remains. Surface this so
                        // admin can re-assign.
                        <span className="text-[10px] font-mono text-neon-yellow">⚠ deleted (#{assignedId})</span>
                      ) : (
                        <span className="text-[10px] font-mono text-text-tertiary italic">unscoped (all campaigns)</span>
                      )}
                    </td>
                    <td className="font-mono text-xs">{p.qualityScore?.toFixed?.(0) ?? 100}</td>
                    <td><StatusBadge status={p.isActive ? 'active' : 'archived'} /></td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Link2 size={12} />}
                        onClick={() => setLinksFor({ id: p.id, subId: p.subId, campaignIds: p.campaignIds })}
                        title="Get tracking links"
                      >
                        Get Link
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Pencil size={12} />}
                        onClick={() => setEditFor(p)}
                        title="Edit publisher (assigned campaign, display name)"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={12} />}
                        onClick={() => handleArchiveOrDelete(p)}
                        title="Delete permanently"
                        className="text-neon-red hover:opacity-70"
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-tertiary">
                    <UserCircle className="mx-auto mb-2" size={32} />
                    <p className="font-mono text-xs">no sub-publishers yet</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New Sub-Publisher">
        <PublisherForm
          onSubmit={(d: any) => create.mutate(d)}
          onCancel={() => setOpen(false)}
          loading={create.isPending}
        />
      </Modal>

      {/* Edit modal — same form, pre-filled with existing publisher data.
          sub_id is locked because changing it would break tracking links
          already shared in the wild. */}
      <Modal
        open={!!editFor}
        onClose={() => setEditFor(null)}
        title={editFor ? `Edit Sub-Publisher: ${editFor.subId}` : 'Edit Sub-Publisher'}
      >
        {editFor && (
          <PublisherForm
            initial={editFor}
            onSubmit={(d: any) => update.mutate({ id: editFor.id, data: d })}
            onCancel={() => setEditFor(null)}
            loading={update.isPending}
          />
        )}
      </Modal>

      {linksFor && (
        <TrackingLinksModal
          publisher={linksFor}
          onClose={() => setLinksFor(null)}
        />
      )}
    </div>
  );
}

interface CampaignOption {
  id: number;
  slug: string;
  displayName: string;
  status: string;
}

function PublisherForm({
  onSubmit,
  onCancel,
  loading,
  initial,
}: {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading: boolean;
  /** Present in edit mode — pre-fill fields. sub_id becomes read-only. */
  initial?: PublisherRow | null;
}) {
  const isEdit = !!initial;
  const [subId, setSubId] = useState(initial?.subId ?? '');
  const [name, setName] = useState(initial?.displayName ?? '');
  // Single campaign assignment per publisher. null = no restriction (all
  // live campaigns available in TrackingLinks). Multi-campaign per publisher
  // is intentionally NOT supported — the typical workflow is "one publisher
  // promotes one product/campaign", and the join-table schema we use stays
  // forward-compatible if that ever changes.
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption | null>(null);
  const [campaignSearch, setCampaignSearch] = useState('');
  // Dropdown visibility — closed by default, opens when input focused or
  // when user types. Closes after selecting or clicking outside.
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const campaigns = useQuery({
    queryKey: ['campaigns-live'],
    queryFn: () => apiClient.get<CampaignOption[]>('/api/v1/campaigns'),
  });
  const liveCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'live');

  // Edit-mode hydration: pre-select the publisher's existing campaign once
  // both `initial.campaignIds` and the campaigns list are available. Runs
  // once because campaigns.data flips from undefined → array on first load
  // and the initial.campaignIds reference is stable for the modal's lifetime.
  const initialCampaignId = initial?.campaignIds?.[0];
  useEffect(() => {
    if (!initialCampaignId || campaigns.data == null) return;
    const found = liveCampaigns.find((c) => c.id === initialCampaignId);
    if (found) setSelectedCampaign(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampaignId, campaigns.data]);

  // Filter the dropdown options by the search query — slug or displayName,
  // case-insensitive. Up to 8 results so dropdown stays compact.
  const filteredCampaigns = (() => {
    const q = campaignSearch.trim().toLowerCase();
    if (!q) return liveCampaigns.slice(0, 8);
    return liveCampaigns
      .filter((c) => c.displayName.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q))
      .slice(0, 8);
  })();

  function pickCampaign(c: CampaignOption) {
    setSelectedCampaign(c);
    setCampaignSearch('');
    setDropdownOpen(false);
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isEdit) {
          // Edit: omit fields PATCH treats as "don't touch" semantics.
          // Don't send subId — backend doesn't update slug from PATCH (would
          // break tracking links). Always send campaignIds for replace semantics.
          onSubmit({
            displayName: name,
            campaignIds: selectedCampaign ? [selectedCampaign.id] : [],
          });
        } else {
          onSubmit({
            subId,
            displayName: name,
            payoutType: 'cpa',
            payoutValue: 0,
            campaignIds: selectedCampaign ? [selectedCampaign.id] : [],
          });
        }
      }}
      className="space-y-4"
    >
      <div className="px-3 py-2 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed">
        💡 Sub-publisher = affiliate yang menjalankan tracking link Anda. Sub ID akan muncul di klik &amp; conversion.
      </div>
      <Input
        label="Sub ID (slug)"
        value={subId}
        onChange={(e) => setSubId(e.target.value)}
        placeholder="rj-manchester"
        hint={isEdit
          ? '🔒 Sub ID dikunci di mode edit — mengubahnya akan mematikan tracking link yang sudah disebar.'
          : 'ID unik yang dipakai di tracking URL ?sub_id=...'}
        disabled={isEdit}
        required
      />
      <Input
        label="Display Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nama publisher (mis. RJ Affiliate)"
      />

      {/* Single-campaign picker. UX nudge: removed "(optional)" label so
          other admins don't skip filling it. Backend still allows empty
          campaign (publisher → all live campaigns) but UI no longer signals
          that as the default expectation. */}
      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          Campaign
        </label>
        {selectedCampaign ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary border border-neon-cyan/40 rounded">
            <span className="text-xs font-mono text-neon-cyan font-bold">{selectedCampaign.displayName}</span>
            <span className="text-[10px] font-mono text-text-tertiary">· {selectedCampaign.slug}</span>
            <button
              type="button"
              onClick={() => setSelectedCampaign(null)}
              className="ml-auto text-text-tertiary hover:text-neon-red text-xs"
              aria-label="Clear campaign selection"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              value={campaignSearch}
              onChange={(e) => {
                setCampaignSearch(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              onBlur={() => {
                // Delay close so click on a dropdown item registers before
                // the dropdown disappears (mousedown fires before blur).
                setTimeout(() => setDropdownOpen(false), 150);
              }}
              placeholder="Search campaign by name or slug..."
              icon={<Search size={14} />}
            />
            {dropdownOpen && filteredCampaigns.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-bg-secondary border border-border-subtle rounded shadow-lg">
                {filteredCampaigns.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => {
                      // mousedown fires BEFORE blur — onClick wouldn't run
                      // because blur would close the dropdown first.
                      e.preventDefault();
                      pickCampaign(c);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-bg-tertiary text-[11px] font-mono flex items-center gap-2"
                  >
                    <span className="text-text-primary">{c.displayName}</span>
                    <span className="text-text-tertiary">· {c.slug}</span>
                  </button>
                ))}
              </div>
            )}
            {dropdownOpen && filteredCampaigns.length === 0 && campaignSearch && (
              <div className="absolute z-10 left-0 right-0 mt-1 px-3 py-2 bg-bg-secondary border border-border-subtle rounded text-[10px] font-mono text-text-tertiary">
                No campaign matches "{campaignSearch}"
              </div>
            )}
          </div>
        )}
        <p className="text-[10px] text-text-tertiary mt-1 font-mono">
          {selectedCampaign
            ? `Tracking links akan dibatasi ke campaign "${selectedCampaign.displayName}" (semua domain tetap muncul untuk failover).`
            : '⚠ Tidak dipilih = sub-publisher punya akses ke SEMUA campaign live.'}
        </p>
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" loading={loading} icon={isEdit ? <Pencil size={14} /> : <Plus size={14} />}>
          {isEdit ? 'Save Changes' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Tracking Links Modal ─────────────────────────────────────────────────────

interface Domain {
  id: number;
  hostname: string;
  status: string;
  isDefault: boolean;
}

interface Campaign {
  id: number;
  slug: string;
  displayName: string;
  status: string;
}

function TrackingLinksModal({
  publisher,
  onClose,
}: {
  publisher: { id: number; subId: string; campaignIds?: number[] };
  onClose: () => void;
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // Search input only relevant when publisher has NO assigned campaign
  // (legacy/unscoped publisher → falls back to old all-campaigns × all-domains
  // view, search helps narrow that down).
  const [search, setSearch] = useState('');

  const domains = useQuery({
    queryKey: ['tracker-domains-active'],
    queryFn: () => apiClient.get<Domain[]>('/api/v1/tracker-domains'),
  });
  const campaigns = useQuery({
    queryKey: ['campaigns-live'],
    queryFn: () => apiClient.get<Campaign[]>('/api/v1/campaigns'),
  });

  const activeDomains = (domains.data ?? []).filter((d) => d.status === 'active' || d.status === 'pending_dns' || d.status === 'pending_ssl');
  const liveCampaigns = (campaigns.data ?? []).filter((c) => c.status === 'live');

  // Resolve the publisher's assigned campaign (single — we changed UX from
  // multi to one-campaign-per-publisher). null = no assignment, fall back
  // to legacy all-campaigns view for backward compat.
  const assignedCampaignId = publisher.campaignIds?.[0];
  const assignedCampaign = assignedCampaignId != null
    ? liveCampaigns.find((c) => c.id === assignedCampaignId) ?? null
    : null;

  const onCopy = (url: string, key: string) => {
    copyToClipboard(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Two layout modes:
  //   1. ASSIGNED   → 1 campaign × N domains (one row per domain, for failover)
  //   2. UNASSIGNED → N campaigns × M domains (legacy cartesian, with search filter)
  // The assigned mode is what user explicitly asked for: "1 campaign,
  // beberapa domain yang tersedia". Unassigned = backward compat for
  // publishers created before campaign scoping existed.
  let links: Array<{ key: string; domain: string; isDefault: boolean; campaignName: string; campaignSlug: string; url: string }> = [];
  if (assignedCampaign) {
    links = activeDomains.map((d) => ({
      key: `${d.id}-${assignedCampaign.id}`,
      domain: d.hostname,
      isDefault: d.isDefault,
      campaignName: assignedCampaign.displayName,
      campaignSlug: assignedCampaign.slug,
      url: `https://${d.hostname}/${assignedCampaign.slug}?sub_id=${encodeURIComponent(publisher.subId)}`,
    }));
  } else {
    const filteredCampaigns = (() => {
      const q = search.trim().toLowerCase();
      if (!q) return liveCampaigns;
      return liveCampaigns.filter((c) =>
        c.displayName.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
      );
    })();
    links = filteredCampaigns.flatMap((c) =>
      activeDomains.map((d) => ({
        key: `${d.id}-${c.id}`,
        domain: d.hostname,
        isDefault: d.isDefault,
        campaignName: c.displayName,
        campaignSlug: c.slug,
        url: `https://${d.hostname}/${c.slug}?sub_id=${encodeURIComponent(publisher.subId)}`,
      })),
    );
  }

  return (
    <Modal open onClose={onClose} title={`Tracking Links: ${publisher.subId}`} size="xl">
      <div className="space-y-4">
        <div className="px-3 py-2 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed">
          💡 Kasih link berikut ke sub-publisher. Saat mereka kirim traffic ke link ini, klik akan ter-track dengan{' '}
          <code className="text-neon-cyan">sub_id={publisher.subId}</code> dan otomatis di-redirect ke offer sesuai routing rule.
        </div>

        {/* When publisher has assigned campaign, show campaign chip + domain
            count summary. No search needed (1 campaign × N domains is
            already compact). */}
        {assignedCampaign ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary border border-neon-cyan/40 rounded">
            <span className="text-[10px] font-display uppercase tracking-wider text-text-tertiary">Campaign:</span>
            <span className="text-xs font-mono text-neon-cyan font-bold">{assignedCampaign.displayName}</span>
            <span className="text-[10px] font-mono text-text-tertiary">· {assignedCampaign.slug}</span>
            <span className="ml-auto text-[10px] font-mono text-text-tertiary">
              {activeDomains.length} domain{activeDomains.length === 1 ? '' : 's'} (failover ready)
            </span>
          </div>
        ) : (
          // Unassigned publisher → show search input as fallback for the
          // cartesian-product view. Surface a hint that explains the user
          // can edit the publisher to scope it.
          <>
            <div className="px-3 py-2 border border-neon-yellow/30 bg-neon-yellow/5 rounded text-[10px] font-mono text-text-secondary leading-relaxed">
              ⚠ Sub-publisher ini belum di-assign ke campaign tertentu — semua campaign × semua domain ditampilkan. Edit publisher untuk pilih campaign spesifik.
            </div>
            <div className="flex-1">
              <Input
                placeholder="Search campaign by name or slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<Search size={14} />}
              />
            </div>
          </>
        )}

        {/* Counter line — adapts to mode. */}
        <p className="text-[10px] font-mono text-text-tertiary">
          {assignedCampaign
            ? `Showing ${links.length} link${links.length === 1 ? '' : 's'} for "${assignedCampaign.displayName}" across ${activeDomains.length} domain${activeDomains.length === 1 ? '' : 's'}`
            : `Showing ${links.length} link${links.length === 1 ? '' : 's'}${search ? ` (filtered: "${search}")` : ''}`}
        </p>

        {links.length === 0 ? (
          <div className="cyber-panel p-8 text-center text-text-tertiary">
            <Link2 className="mx-auto mb-2 opacity-50" size={32} />
            {liveCampaigns.length === 0 ? (
              <p className="font-mono text-xs">
                Belum ada campaign live. Buat <a href="/campaigns" className="text-neon-cyan hover:underline">campaign</a> dulu + setup routing rule.
              </p>
            ) : activeDomains.length === 0 ? (
              <p className="font-mono text-xs">
                Belum ada domain aktif. Setup <a href="/tracker-domains" className="text-neon-cyan hover:underline">tracker domain</a> dulu.
              </p>
            ) : assignedCampaign ? (
              <p className="font-mono text-xs">
                Campaign "{assignedCampaign.displayName}" tidak punya domain aktif.
              </p>
            ) : search ? (
              <p className="font-mono text-xs">No campaign matches "{search}"</p>
            ) : (
              <p className="font-mono text-xs">No links available.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {links.map((l) => (
              // BUGFIX: previously `flex items-center` made the COPY button
              // visually drift because the middle column has 2 stacked lines
              // (header + URL) while the button is single-line — items-center
              // was technically correct but looked unaligned against the URL
              // line, and the button's outer glow overflowed the panel border.
              // Restructure: header on row 1, URL+button on row 2 with the
              // button aligned to the URL's vertical axis. shrink-0 prevents
              // the button from being compressed by long URLs.
              <div key={l.key} className="cyber-panel p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Link2 size={14} className="text-neon-cyan shrink-0" />
                  <span className="font-display text-xs font-bold">{l.campaignName}</span>
                  <span className="text-[10px] text-text-tertiary font-mono">@ {l.domain}</span>
                  {l.isDefault && (
                    <span className="text-[9px] font-mono text-neon-cyan border border-neon-cyan/40 px-1.5 py-0.5 rounded">DEFAULT</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 text-[11px] font-mono text-neon-cyan bg-bg-tertiary px-2 py-1.5 rounded break-all">
                    {l.url}
                  </code>
                  <Button
                    className="shrink-0"
                    variant={copiedKey === l.key ? 'magenta' : 'neon'}
                    size="sm"
                    icon={copiedKey === l.key ? <CheckCircle size={12} /> : <Copy size={12} />}
                    onClick={() => onCopy(l.url, l.key)}
                  >
                    {copiedKey === l.key ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
